import { useState, useEffect } from 'react'
import { Product, PricingOption, SizeConstraint, CutToSizeGroupModel } from './types/models'

type Props = {
  groups: CutToSizeGroupModel[]
  dimension: string
  thickness: string
}

function CutToSizeLogic ({ groups, dimension, thickness }: Props) {
  const VAT_RATE = 1.2
  const WIDTH = 'Width'
  const LENGTH = 'Length'
  const MM_IN_INCH = 25.4

  const [currentGroup, setCurrentGroup] = useState<CutToSizeGroupModel>(groups.find((group) => group.products.length > 0) ?? groups[0])
  const [selectedProduct, setSelectedProduct] = useState<Product>(null)

  const [constraints, setConstraints] = useState<SizeConstraint[]>([])
  const [lengthConstraint, setLengthConstraint] = useState<SizeConstraint>(null)
  const [widthConstraint, setWidthConstraint] = useState<SizeConstraint>(null)

  const [selectedThickness, setSelectedThickness] = useState<string>()
  const [selectedDimension, setSelectedDimension] = useState<string>()
  const [validThicknesses, setValidThicknesses] = useState<string[]>([])

  const [pricingOption, setPricingOption] = useState<PricingOption>()

  const [originalPrice, setOriginalPrice] = useState<number>(0)
  const [price, setPrice] = useState<number>(0)

  const [unitForLength, setUnitForLength] = useState<string>('mm')
  const [unitForWidth, setUnitForWidth] = useState<string>('mm')

  // Length and width are both stored according to what the unit is.
  const [length, setLength] = useState<number>(0)
  const [width, setWidth] = useState<number>(0)
  const [quantity, setQuantity] = useState<number | null>(1)

  const [lengthMax, setLengthMax] = useState<number>()
  const [lengthMin, setLengthMin] = useState<number>()
  const [lengthStep, setLengthStep] = useState<string>('1')

  const [widthMax, setWidthMax] = useState<number>()
  const [widthMin, setWidthMin] = useState<number>()
  const [widthStep, setWidthStep] = useState<string>('1')

  const [canSubmit, setCanSubmit] = useState<boolean>(false)

  const UNITS = { mm: 1, cm: 10, m: 1000 }

  const usesThickness = (): boolean => currentGroup.thicknessField !== null

  const resetState = () => {
    setPrice(0)
    setLength(0)
    setWidth(0)
    setQuantity(1)
  }

  // Orders by either integers or integers with fractions
  const sortArray = (array: string[]): string[] => {
    if (!array) return []

    return array.sort((a, b) => {
      const _a = convertDimensionToNumber(a)
      const _b = convertDimensionToNumber(b)

      return _a - _b
    })
  }

  // Always convert the dimension to MM before sorting. This ensures that if there
  // is a mix of metric and imperial units, they are in order of size regardless.
  const convertDimensionToNumber = (value: string) => {
    const mmRegex = /^(\d*[.]?[\d]*)mm/
    const mm = value.match(mmRegex)
    if (mm) {
      return parseFloat(mm[1])
    }

    const regex = /^(?<singleFraction>\d+\/\d+)|^(?<wholeNumber>\d+)? ?(?<fraction>\d+\/\d+)?/

    const match = value.match(regex)

    if (match.groups.singleFraction) return convertFractionToMM(match.groups.singleFraction)

    const singleNum = parseInt(match.groups.wholeNumber)
    const inchesInMM = singleNum * MM_IN_INCH
    const fraction = convertFractionToMM(match.groups.fraction)
    return inchesInMM + fraction
  }

  const convertFractionToMM = (fraction: string): number => {
    if (!fraction) return 0

    const split = fraction.split('/')
    if (split.length === 1) {
      return parseFloat(split[0]) * MM_IN_INCH
    }

    const top = parseFloat(split[0])
    const bottom = parseFloat(split[1])
    return top / bottom * MM_IN_INCH
  }

  const calculateProduct = (newThickness: string, newDimension: string) => {
    // Calculate product
    const product = currentGroup.products.find((p) => {
      if (p.dimension !== newDimension) {
        return false
      }

      // If the profile does not call for a thickness
      if (!usesThickness()) {
        return true
      }

      if (p.thickness !== newThickness) {
        return false
      }

      return true
    })
    setSelectedProduct(product)
  }

  const triggerProductUpdate = () => {
    const thicknesses = sortArray(currentGroup.dimensions[selectedDimension])
    setValidThicknesses(thicknesses)
    if (usesThickness()) {
      const newThickness = (thicknesses.includes(thickness)) ? thickness : thicknesses[0]
      setSelectedThickness(newThickness)
      calculateProduct(newThickness, selectedDimension)
    } else {
      calculateProduct('', selectedDimension)
    }
  }

  // The pricing calculators here are a reimplementation of the pricing calculators in Ruby
  // lib/price_calculator/cut_to_size_length.rb
  const priceForTube = (option: PricingOption) => {
    const newPrice = (sizeInMM(length, unitForLength) * option.pricePerM * quantity) / 1000
    const originalPrice = (sizeInMM(length, unitForLength) * option.originalPrice * quantity) / 1000
    setPrice(newPrice)
    setOriginalPrice(originalPrice)
  }

  // lib/price_calculator/sheet.rb
  const priceForSheet = (option: PricingOption) => {
    const area = (sizeInMM(length, unitForLength) * sizeInMM(width, unitForWidth)) / 1000000
    const newPrice = area * option.pricePerM * quantity
    const originalPrice = area * option.originalPrice * quantity
    setPrice(newPrice)
    setOriginalPrice(originalPrice)
  }

  const sizeInMM = (size: number, unit: string): number => {
    let sizeMM = size
    if (unit === 'm') {
      sizeMM *= 1000
    } else if (unit === 'cm') {
      sizeMM *= 10
    }
    return sizeMM
  }

  const isSheet = (): boolean => selectedProduct.pricingMethod === 'sheet'

  const isTube = (): boolean => ['cut_to_size_length', 'galvanised_tube'].includes(selectedProduct.pricingMethod)

  const usesSize = (): boolean => currentGroup.profile !== 'Sheet'

  const isWidth = (field: string) => field === WIDTH

  const isLength = (field: string) => field === LENGTH

  const isValid = (): boolean => {
    if (!selectedProduct) return false
    // If the quantity is less than one it will not be added to the basket anyway
    if (quantity <= 0) return true
    return isSheet() ? calculateValidSize(true) && calculateValidSize(false) : calculateValidSize(true)
  }

  const calculateValidSize = (isLength: boolean) => {
    // We do this because we do not want to show a red outline and error message on first page load
    // canSubmit is false until the user interacts with the cutting bar.
    if (!canSubmit) return true

    const size = isLength ? length : width
    const min = isLength ? lengthMin : widthMin
    const max = isLength ? lengthMax : widthMax

    return (size >= min && size <= max)
  }

  const onDimensionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedDimension = event.target.value
    setSelectedDimension(selectedDimension)
    toggleSubmittable()
  }

  const onThicknessChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedThickness = event.target.value
    setSelectedThickness(selectedThickness)
    toggleSubmittable()
  }

  const onGroupChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newGroup = groups.find((ctg) => ctg.name === event.target.value)
    setCurrentGroup(newGroup)
  }

  const onSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseFloat(event.target.value) || 0
    const { field } = event.target.dataset

    const countDecimals = (value: (string | number)): number => {
      value = value.toString()
      return (value && value.split('.')[1]?.length) || 0
    }
    const unit = isLength(field) ? unitForLength : unitForWidth
    if (unit === 'mm' && countDecimals(event.target.value) > 0) {
      return
    }
    if (unit === 'cm' && countDecimals(event.target.value) > 1) {
      return
    }
    if (unit === 'm' && countDecimals(event.target.value) > 3) {
      return
    }

    if (isLength(field)) {
      setLength(newSize)
    } else if (isWidth(field)) {
      setWidth(newSize)
    }

    toggleSubmittable()
  }

  const onUnitChanged = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const unit = event.target.value
    const { field } = event.target.dataset

    const newUnitStep = UNITS[unit]

    if (isLength(field)) {
      const oldUnitStep = UNITS[unitForLength]
      const newLength = length * (oldUnitStep / newUnitStep)
      setUnitForLength(unit)
      setLength(Math.round(newLength * newUnitStep) / newUnitStep)
      setLengthMin(lengthConstraint.min / newUnitStep)
      setLengthMax(lengthConstraint.max / newUnitStep)
      setLengthStep((1 / newUnitStep).toString())
    } else if (isWidth(field)) {
      const oldUnitStep = UNITS[unitForWidth]
      const newWidth = width * (oldUnitStep / newUnitStep)
      setUnitForWidth(unit)
      setWidth(Math.round(newWidth * newUnitStep) / newUnitStep)
      setWidthMin(widthConstraint.min / newUnitStep)
      setWidthMax(widthConstraint.max / newUnitStep)
      setWidthStep((1 / newUnitStep).toString())
    }

    toggleSubmittable()
  }

  const onQuantityChanged = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    const quantity = parseInt(value, 10) || null
    setQuantity(quantity)
  }

  const hasProducts = (): boolean => {
    return currentGroup.products.length > 0
  }

  // The quantity is defaulted to 1. On initial page load, if the user has not interacted with the cutting bar we don't
  // want to submit it as it will be in an invalid state, which will show an error
  const toggleSubmittable = (): void => {
    if (!canSubmit) {
      setCanSubmit(true)
    }
  }

  const calculatePricingOption = (): PricingOption => {
    const availableOptions = [...selectedProduct.pricingOptions].reverse()

    const option = availableOptions.find((option) => {
      if (isTube()) {
        const optionLength = option.sizes[0].max
        return sizeInMM(length, unitForLength) <= optionLength
      } else {
        const optionLength = option.sizes[0].max
        const optionWidth = option.sizes[1].max
        return sizeInMM(length, unitForLength) <= optionLength && sizeInMM(width, unitForWidth) <= optionWidth
      }
    })
    return option || availableOptions[0]
  }

  useEffect(() => {
    globalThis.clearCuttingBarInputs = () => {
      resetState()
      setCanSubmit(false)
    }
  }, [])

  useEffect(() => {
    const dimensions = sortArray(Object.keys(currentGroup.dimensions))
    const newDimension = (dimensions.includes(dimension)) ? dimension : dimensions[0]
    if (newDimension === selectedDimension) {
      // Force an update of the current product
      triggerProductUpdate()
    } else {
      setSelectedDimension(newDimension)
    }
  }, [currentGroup])

  useEffect(() => {
    resetState()
    setUnitForLength('mm')
    setUnitForWidth('mm')
    if (selectedProduct) {
      setPricingOption(selectedProduct.pricingOptions[0])

      const { sizeConstraints } = selectedProduct
      setConstraints(sizeConstraints)
      const lConstraint = sizeConstraints.find((sc) => sc.field === LENGTH)
      const wConstraint = sizeConstraints.find((sc) => sc.field === WIDTH)
      setLengthConstraint(lConstraint)
      setWidthConstraint(wConstraint)

      if (lConstraint) {
        setLengthMax(lConstraint.max)
        setLengthMin(lConstraint.min)
      }

      if (wConstraint) {
        setWidthMax(wConstraint.max)
        setWidthMin(wConstraint.min)
      }
    }
  }, [selectedProduct])

  useEffect(() => {
    calculateProduct(selectedThickness, selectedDimension)
  }, [selectedThickness])

  useEffect(() => {
    triggerProductUpdate()
  }, [selectedDimension])

  useEffect(() => {
    if (selectedProduct) {
      const newPricingOption = calculatePricingOption()
      setPricingOption(newPricingOption)

      const valid = isValid()

      if (!valid) {
        setPrice(0)
      } else {
        // Calculate the price
        if (isTube()) {
          priceForTube(newPricingOption)
        } else if (isSheet()) {
          priceForSheet(newPricingOption)
        }
      }
    }
  }, [length, width, quantity])

  return {
    VAT_RATE,
    WIDTH,
    LENGTH,
    currentGroup,
    selectedProduct,
    constraints,
    selectedThickness,
    selectedDimension,
    validThicknesses,
    pricingOption,
    originalPrice,
    price,
    unitForLength,
    unitForWidth,
    length,
    width,
    quantity,
    setQuantity,
    lengthMax,
    lengthMin,
    lengthStep,
    widthMax,
    widthMin,
    widthStep,
    canSubmit,
    UNITS,
    usesThickness,
    sortArray,
    usesSize,
    isValid,
    sizeInMM,
    calculateValidSize,
    onDimensionChange,
    onThicknessChange,
    onGroupChange,
    onSizeChange,
    onUnitChanged,
    onQuantityChanged,
    hasProducts,
    resetState
  }
}

export default CutToSizeLogic
